import {ChevronRightIcon, type IconProps} from '@primer/octicons-react'
import {ActionList, useRefObjectAsForwardedRef} from '@primer/react'
import {isValidElementType} from 'react-is'
import {
  type AriaRole,
  type ComponentProps,
  type ElementType,
  type FunctionComponent,
  type KeyboardEventHandler,
  type PropsWithChildren,
  type ReactNode,
  forwardRef,
  useRef,
} from 'react'

type ActionListItemProps = ComponentProps<(typeof ActionList)['Item']>

export type MenuItemAnchorProps = Omit<ActionListItemProps, 'role'> & {
  // Linting prevents us from just using `ActionList.LeadingVisual` in children (even though it would work in this
  // case) so this prop provides a slot prop to use instead.
  /**
   * The element to render within the item before its contents. Nothing is shown by default.
   */
  leadingVisual?: ElementType | ReactNode
  /**
   * The icon to render within the item but after its contents. Defaults to a chevron pointing right.
   */
  trailingIcon?: FunctionComponent<PropsWithChildren<IconProps>>
  // PRC's internal `AriaRole` type was created to polyfill `React.AriaRole` and now they are incompatible because
  // more roles exist in HTML than PRC has listed. This would prevent this component from accepting the `props` from
  // `SelectPanel#renderAnchor` so we have to fix the type explicitly by redefining & casting it.
  role?: AriaRole
}

/**
 * Lightweight wrapper around `ActionList.Item` intended for use as the anchor for `NestableActionMenu`s placed inside
 * other `NestableActionMenu`s. Renders a right-facing chevron icon as trailing visual and activates with
 * right arrow key in addition to enter.
 */
export const MenuItemAnchor = forwardRef<HTMLLIElement, MenuItemAnchorProps>(
  (
    {
      children,
      leadingVisual: LeadingVisual,
      trailingIcon: TrailingIcon = ChevronRightIcon,
      onKeyDown: externalOnKeyDown,
      ...props
    },
    forwardedRef,
  ) => {
    const ref = useRef<HTMLLIElement>(null)
    useRefObjectAsForwardedRef(forwardedRef, ref)

    /** Treat right arrow key press as click. */
    const onKeyDown: KeyboardEventHandler<HTMLLIElement> = event => {
      externalOnKeyDown?.(event)

      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.key === 'ArrowRight' && !event.defaultPrevented) ref.current?.click()
    }

    return (
      <ActionList.Item
        {...props}
        onKeyDown={onKeyDown}
        ref={ref}
        role={(props.role as ActionListItemProps['role']) ?? 'menuitem'}
      >
        {LeadingVisual && (
          <ActionList.LeadingVisual>
            {typeof LeadingVisual !== 'string' && isValidElementType(LeadingVisual) ? <LeadingVisual /> : LeadingVisual}
          </ActionList.LeadingVisual>
        )}

        {children}

        <ActionList.TrailingVisual>
          <TrailingIcon />
        </ActionList.TrailingVisual>
      </ActionList.Item>
    )
  },
)
MenuItemAnchor.displayName = 'NestableActionMenu.MenuItemAnchor'
